/**
 * Reading and writing browser cookies.
 *
 * Only what this application needs: no expiry parsing, no signing, no server
 * side. Everything here operates on `document.cookie`, so it is client-only by
 * construction.
 */

export interface CookieOptions {
  /** Lifetime in seconds. Omitted makes it a session cookie. */
  maxAge?: number
  sameSite?: 'lax' | 'strict' | 'none'
  secure?: boolean
  /** Sends the cookie to subdomains too. Must be a suffix of the current host. */
  domain?: string
  path?: string
}

export function readCookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length))
    }
  }
  return null
}

export function writeCookie(
  name: string,
  value: string,
  options: CookieOptions = {},
): void {
  const { maxAge, sameSite = 'lax', secure = false, domain, path = '/' } = options

  const parts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    `Path=${path}`,
  ]
  if (maxAge !== undefined) parts.push(`Max-Age=${String(maxAge)}`)
  if (domain !== undefined) parts.push(`Domain=${domain}`)
  parts.push(`SameSite=${sameSite}`)
  // `SameSite=None` is rejected outright without `Secure`, so a deployment
  // configured for cross-site embedding over plain http would silently set no
  // cookie at all rather than an insecure one.
  if (secure || sameSite === 'none') parts.push('Secure')

  document.cookie = parts.join('; ')
}

/**
 * Removes a cookie.
 *
 * The attributes have to match the ones it was written with -- a cookie set on
 * the registrable domain is a different cookie from the one on the host, and
 * expiring the wrong one leaves the other in place and still being sent.
 */
export function deleteCookie(
  name: string,
  options: Pick<CookieOptions, 'domain' | 'path'> = {},
): void {
  const { domain, path = '/' } = options
  const parts = [`${encodeURIComponent(name)}=`, `Path=${path}`, 'Max-Age=0']
  if (domain !== undefined) parts.push(`Domain=${domain}`)
  document.cookie = parts.join('; ')
}
